// Email: the SMTP server Tendwell sends invitations, reset links and notices through, with a test button
// and the list of recent messages.
import { api } from "../lib/api";
import { brand } from "../lib/brand";
import { fmt, t } from "../lib/i18n";
import { h, invalidate, local, resource } from "../lib/ui";
import { act, Async, Button, Chip, Empty, Field, isBusy, Select, toast } from "../shared/components";
import { PasswordInput } from "../shared/auth";
import { PageHead } from "./app";

function readForm(form: HTMLFormElement) {
  const fd = new FormData(form);
  const str = (k: string) => {
    const v = String(fd.get(k) ?? "").trim();
    return v === "" ? null : v;
  };
  const port = str("port");
  return {
    enabled: fd.get("enabled") === "on",
    host: str("host"), port: port ? Number(port) : null, security: str("security") ?? "starttls",
    username: str("username"), password: (fd.get("password") as string) || null,
    from_email: str("from_email"), from_name: str("from_name"), reply_to: str("reply_to"),
  };
}

export function EmailPage() {
  const r = resource("admin.mail", () => api.get("/api/admin/mail"));
  const s = local("screen:email", () => ({ security: "" }));
  return (
    <div class="stack loose">
      <PageHead title={t("admin.email.title")} intro={t("admin.email.intro")} />
      {Async(r, (d: any) => {
        const st = d.settings;
        const security = s.security || st.security;
        const ready = st.enabled && st.host && st.port && st.from_email;
        return (
          <div class="stack loose">
            <div class="cols-2" style="align-items:start">
              <form class="panel" onSubmit={(e: Event) => {
                e.preventDefault();
                const form = e.currentTarget as HTMLFormElement;
                const body = readForm(form);
                act("mail.save", async () => {
                  const res = await api.put("/api/admin/mail", body);
                  const on = !!(res.settings.enabled && res.settings.host && res.settings.port && res.settings.from_email);
                  brand.set({ current: { ...brand.current, emailEnabled: on } });
                  (form.elements.namedItem("password") as HTMLInputElement).value = "";
                  invalidate("admin.mail");
                }, t("admin.email.saved"));
              }}>
                <div class="panel-head row between">
                  <h2>{t("admin.email.server")}</h2>
                  <Chip kind={ready ? "good" : "neutral"}>{ready ? t("admin.email.on") : t("admin.email.off")}</Chip>
                </div>
                <div class="panel-body stack">
                  <label class="check">
                    <input type="checkbox" name="enabled" checked={st.enabled} />
                    <span class="strong">{t("admin.email.enabled")}</span>
                  </label>
                  <Field label={t("admin.email.host")} id="m-host" hint={t("admin.email.hostHint")}>
                    <input class="input" id="m-host" name="host" value={st.host ?? ""} autocomplete="off" spellcheck={false} dir="ltr" />
                  </Field>
                  <div class="cols-2" style="grid-template-columns:2fr 1fr">
                    <Field label={t("admin.email.security")} id="m-sec">
                      <Select id="m-sec" name="security" value={security} onChange={(v) => s.set({ security: v })} options={[
                        { value: "tls", label: t("admin.email.securityTls") },
                        { value: "starttls", label: t("admin.email.securityStarttls") },
                        { value: "none", label: t("admin.email.securityNone") },
                      ]} />
                    </Field>
                    <Field label={t("admin.email.port")} id="m-port">
                      <input class="input" id="m-port" name="port" inputMode="numeric" value={st.port ?? (security === "tls" ? "465" : "587")} dir="ltr" />
                    </Field>
                  </div>
                  <div class="cols-2">
                    <Field label={t("admin.email.username")} id="m-user">
                      <input class="input" id="m-user" name="username" value={st.username ?? ""} autocomplete="off" spellcheck={false} dir="ltr" />
                    </Field>
                    <Field label={t("admin.email.password")} id="m-pass" hint={st.hasPassword ? t("admin.email.passwordKeep") : t("admin.email.passwordHint")}>
                      <PasswordInput id="m-pass" name="password" autocomplete="new-password" placeholder={st.hasPassword ? "••••••••" : ""} />
                    </Field>
                  </div>
                  <div class="divider" />
                  <h3 style="margin:0">{t("admin.email.sender")}</h3>
                  <div class="cols-2">
                    <Field label={t("admin.email.fromEmail")} id="m-from" hint={t("admin.email.fromEmailHint")}>
                      <input class="input" id="m-from" name="from_email" type="email" value={st.from_email ?? ""} dir="ltr" />
                    </Field>
                    <Field label={t("admin.email.fromName")} id="m-fromname">
                      <input class="input" id="m-fromname" name="from_name" value={st.from_name ?? brand.current.appName} />
                    </Field>
                  </div>
                  <Field label={t("admin.email.replyTo")} id="m-reply" hint={t("admin.email.replyToHint")}>
                    <input class="input" id="m-reply" name="reply_to" type="email" value={st.reply_to ?? ""} dir="ltr" />
                  </Field>
                  <div class="row between" style="flex-wrap:wrap;gap:8px">
                    <Button kind="secondary" icon="mail" busy={isBusy("mail.test")} onClick={(e: Event) => {
                      const form = (e.currentTarget as HTMLElement).closest("form") as HTMLFormElement;
                      const body = readForm(form);
                      act("mail.test", async () => {
                        const res = await api.post("/api/admin/mail/test", body);
                        toast(t("admin.email.testSent", { email: res.to }));
                        invalidate("admin.mail");
                      });
                    }}>{t("admin.email.test")}</Button>
                    <Button type="submit" busy={isBusy("mail.save")}>{t("admin.email.save")}</Button>
                  </div>
                </div>
              </form>
              <aside class="panel">
                <div class="panel-head"><h2>{t("admin.email.tipTitle")}</h2></div>
                <div class="panel-body stack small">
                  <p style="margin:0">{t("admin.email.tipGmail")}</p>
                  <p style="margin:0">{t("admin.email.tipMs")}</p>
                  <p style="margin:0" class="muted">{t("admin.email.tipOther")}</p>
                </div>
              </aside>
            </div>
            <section class="panel">
              <div class="panel-head"><h2>{t("admin.email.recent")}</h2></div>
              {d.recent.length ? (
                <div class="table-wrap">
                  <table class="table">
                    <thead><tr><th>{t("admin.email.when")}</th><th>{t("admin.email.to")}</th><th>{t("admin.email.subject")}</th><th>{t("admin.email.status")}</th></tr></thead>
                    <tbody>
                      {d.recent.map((m: any) => (
                        <tr>
                          <td class="small muted">{fmt.relative(m.created_at)}</td>
                          <td class="small" dir="ltr">{m.to_email}<span class="sub">{t(`admin.email.kinds.${m.kind}`)}</span></td>
                          <td class="small">{m.subject}</td>
                          <td>
                            <Chip kind={m.status === "sent" ? "good" : m.status === "failed" ? "critical" : "accent"}>{t(`admin.email.mstatus.${m.status}`)}</Chip>
                            {m.last_error && m.status !== "sent" ? <span class="sub" style="max-width:280px;white-space:normal">{m.last_error}</span> : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <Empty title={t("admin.email.noRecent")} />}
            </section>
          </div>
        );
      })}
    </div>
  );
}
